import { useState } from 'react';
import { Avatar, Badge, Button, EmptyState, InlineError, Select } from '../../components/ui';
import { Icon } from '../../components/ui/Icon';
import { RowGroup } from '../../components/ui/RowGroup';
import {
  useAddProjectMember,
  useRemoveProjectMember,
  useTapPeople,
  useUpdateProjectMemberRole,
} from './hooks';
import { memberErrorMessage } from './queries';
import { MessagePersonButton } from '../messages/MessagePersonButton';
import { PROJECT_ROLE_LABELS } from './types';
import type { SelectOption } from '../../components/ui';
import type { GrantableRole, ProjectMemberWithProfile, ProjectRole } from './types';

/**
 * Project membership management (Stage 11). Managers — a TAP Admin or the
 * project owner — add active TAP people, switch a member between the Member
 * and Viewer project roles, and remove members. The owner row is shown but
 * never offered controls: project_members_guard() makes it immutable, and
 * ownership is never granted from the browser.
 *
 * Everyone else sees the same list read-only. Nothing here decides
 * permission; can_manage_project() in the database refuses anything the
 * caller may not do.
 */

const ROLE_OPTIONS: SelectOption[] = [
  { value: 'member', label: PROJECT_ROLE_LABELS.member },
  { value: 'viewer', label: PROJECT_ROLE_LABELS.viewer },
];

function personLabel(displayName: string, email: string): string {
  return displayName.trim() !== '' ? displayName : email;
}

export function MembersPanel({
  projectId,
  members,
  canManage,
}: {
  projectId: string;
  members: ProjectMemberWithProfile[];
  /** May manage membership; the database remains the authority. */
  canManage: boolean;
}) {
  const [error, setError] = useState('');
  const [personId, setPersonId] = useState('');
  const [role, setRole] = useState<GrantableRole>('member');
  /**
   * Whether the add form is showing.
   *
   * Closed by default since 3 September. It used to be a permanently open
   * block of two labelled fields and a button hanging off the bottom of the
   * card — 123px of form on every visit to a project, for something that
   * happens once per person per project. Kyu circled it twice. Now the card's
   * last row is "Add member", and pressing it turns that row into the form.
   */
  const [adding, setAdding] = useState(false);

  const people = useTapPeople(canManage);
  const addMember = useAddProjectMember(projectId);
  const updateRole = useUpdateProjectMemberRole(projectId);
  const removeMember = useRemoveProjectMember(projectId);

  const memberIds = new Set(members.map((member) => member.user_id));
  const candidates = (people.data ?? []).filter((person) => !memberIds.has(person.id));

  function report(cause: unknown) {
    setError(memberErrorMessage(cause instanceof Error ? cause.message : ''));
  }

  function submit() {
    if (personId === '') {
      setError('Choose a person to add.');
      return;
    }
    setError('');
    addMember.mutate(
      { userId: personId, projectRole: role },
      {
        // Closes on success, stays open on failure. A form that vanishes when
        // the save was refused takes the message with it and leaves somebody
        // wondering whether it worked.
        onSuccess: () => {
          setPersonId('');
          setRole('member');
          setAdding(false);
        },
        onError: report,
      },
    );
  }

  function cancelAdd() {
    setPersonId('');
    setRole('member');
    setError('');
    setAdding(false);
  }

  function changeRole(member: ProjectMemberWithProfile, projectRole: ProjectRole) {
    setError('');
    updateRole.mutate({ memberId: member.id, projectRole }, { onError: report });
  }

  function remove(member: ProjectMemberWithProfile) {
    setError('');
    removeMember.mutate(member.id, { onError: report });
  }

  return (
    <RowGroup title="Members">
      {error === '' ? null : <InlineError>{error}</InlineError>}

      {members.length === 0 && !canManage ? (
        <EmptyState title="No members yet" />
      ) : (
        /* One row per person in one inset card — the shape Settings, Admin and
           the project's own details all use. (It was a two-column grid of
           tiles until drop 77; the class name is older than the pattern.)

           The add control is the last row of this list rather than a block
           under it, so the card holds everything about membership and nothing
           hangs off its bottom edge. */
        <ul className="ui-row-group-list">
          {members.map((member) => {
            const label = personLabel(member.display_name, member.email);
            const managed = canManage && member.project_role !== 'owner';
            return (
              <li key={member.id} className="member-tile">
                <Avatar name={label} imageUrl={member.avatar_url} size="sm" />
                <span className="member-name">{label}</span>
                {/* 3b-1: start (or reopen) a DM with this person. Renders
                    nothing for yourself, for a viewer or supplier, or for a
                    person a DM cannot hold. */}
                <MessagePersonButton userId={member.user_id} name={label} />
                {managed ? (
                  <span className="member-controls">
                    <select
                      className="task-cell-input member-role"
                      aria-label={`Role for ${label}`}
                      value={member.project_role}
                      onChange={(event) => changeRole(member, event.target.value as ProjectRole)}
                    >
                      {ROLE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="member-remove"
                      aria-label={`Remove ${label}`}
                      title={`Remove ${label}`}
                      onClick={() => remove(member)}
                    >
                      &times;
                    </button>
                  </span>
                ) : (
                  <Badge tone={member.project_role === 'owner' ? 'brand' : 'neutral'}>
                    {PROJECT_ROLE_LABELS[member.project_role]}
                  </Badge>
                )}
              </li>
            );
          })}

          {!canManage ? null : adding ? (
            <li className="member-add-open">
              <Select
                label="Add person"
                labelHidden
                autoFocus
                value={personId}
                onChange={(event) => setPersonId(event.target.value)}
                options={[
                  {
                    value: '',
                    label: candidates.length === 0 ? 'No one available to add' : 'Choose a person',
                  },
                  ...candidates.map((person) => ({
                    value: person.id,
                    label: personLabel(person.display_name, person.email),
                  })),
                ]}
              />
              <span className="member-add-role">
                <Select
                  label="Role"
                  labelHidden
                  value={role}
                  onChange={(event) => setRole(event.target.value as GrantableRole)}
                  options={ROLE_OPTIONS}
                />
              </span>
              <Button size="sm" onClick={submit} disabled={addMember.isPending}>
                {addMember.isPending ? 'Adding…' : 'Add'}
              </Button>
              <Button variant="ghost" size="sm" onClick={cancelAdd}>
                Cancel
              </Button>
            </li>
          ) : (
            <li>
              {/* The row IS the button, the same rule RowGroup's action rows
                  follow: the whole row is the target, so the element that is
                  the row has to be the element that takes the press. */}
              <button type="button" className="member-add-row" onClick={() => setAdding(true)}>
                <Icon name="add" size="md" />
                Add member
              </button>
            </li>
          )}
        </ul>
      )}
      {canManage && people.isError ? (
        <p className="task-panel-hint">The list of people could not be loaded. Try again.</p>
      ) : null}
    </RowGroup>
  );
}
